import React, { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import {
  Autocomplete,
  Stack,
  Typography,
  useTheme,
  Box,
  Popover,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
} from "@mui/material";
import useApi from "../hooks/useApi.js";
import {
  getStatusOptionsArray,
  getYear,
  handleApiError,
} from "../utils/utils.js";
import CloseIcon from "@mui/icons-material/Close";
import MouseIcon from "@mui/icons-material/Mouse";
import InfoIcon from "@mui/icons-material/Info";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import SearchIcon from "@mui/icons-material/Search";
import ButtonBase from "@mui/material/ButtonBase";
import { useDebounce } from "../hooks/useDebounce.js";
import { useAuth } from "../context/authContext.js";

export default function SearchBar({
  contentType,
  onQuickAdd,
  onSelect,
  onClose,
  fullscreen,
  enableQuickAdd = true,
  clearOnSelect = false,
  title,
}) {
  const theme = useTheme();
  const { user, openAuthDialog } = useAuth();
  const statusOptionsArray = getStatusOptionsArray(theme).filter(
    (option) => option.value !== "none",
  );
  const { fetchContentAutocomplete, fetchContentSearch, addContent } = useApi();
  const [options, setOptions] = useState([]); // Autocomplete dropdown options
  const [searchInput, setSearchInput] = useState(""); // Input value of searchbar
  const [focusedOption, setFocusedOption] = useState(null); // Index of focused option
  const [openAutocompleteDropdown, setOpenAutocompleteDropdown] =
    useState(true);
  const [infoPopupAnchorEl, setInfoPopupAnchorEl] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // {mouseX, mouseY, selection}
  const [isLoading, setIsLoading] = useState(false); // loading state for fetchMovieSearch
  const timerRef = useRef(null); // timer for long press
  // True while a finger is down on an option. Android Chrome pulls focus off the
  // input partway through a long press, before the quick-add menu has opened, so
  // the input's onBlur needs this to avoid closing the dropdown underneath it.
  const optionPressRef = useRef(false);
  const inputRef = useRef(null);
  const [longPressedOption, setLongPressedOption] = useState(null); // to enable conditional highlighting when long pressed
  const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0; // to conditionally show help specific to desktop vs mobile

  const debouncedAutocomplete = useDebounce(async (query) => {
    try {
      const data = await fetchContentAutocomplete(query, contentType);
      setOptions([
        ...data,
        {
          // "further" only makes sense when there are results above it
          label: `Search ${data.length > 0 ? "further " : ""}for "${query}"`,
          searchFor: true,
          value: query,
        },
      ]);
    } catch (error) {
      console.error("Error fetching autocomplete data:", error);
    }
  }, 300);

  useEffect(() => {
    const q = searchInput?.trim() ?? "";
    if (q.length < 1) {
      debouncedAutocomplete.cancel();
      setOptions([]);
      return;
    }

    debouncedAutocomplete(q);
    return () => debouncedAutocomplete.cancel();
  }, [searchInput, debouncedAutocomplete]);

  const handleSearchFor = async (searchValue) => {
    try {
      setIsLoading(true);
      setFocusedOption(null);
      setOpenAutocompleteDropdown(false);

      const response = await fetchContentSearch(searchValue, contentType);

      const newOptions = response.map((result) => ({
        title: result.title,
        date: result.date,
        external_id: result.external_id,
        person: result.person,
        image_url: result.image_url, // book results carry an ISBNdb cover id
      }));

      const hint = contentType === "book" ? [{ searchHint: true }] : [];

      if (newOptions.length > 0) {
        setOptions([...newOptions, ...hint]);
      } else {
        setOptions([{ noResults: true, label: "No results found" }, ...hint]);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      setOpenAutocompleteDropdown(true);
    }
  };

  const handleSelect = async (selectedOption) => {
    // if selectedOption has external_id property, content needs to be added to database
    // if it doesn't have external_id then it can be passed to onSelect function directly
    setIsLoading(true);
    setFocusedOption(null);
    setOpenAutocompleteDropdown(false);
    if (selectedOption.external_id) {
      try {
        const response = await addContent({
          contentType,
          identifier: selectedOption.external_id,
          title: selectedOption.title,
          person: selectedOption.person,
          publishedDate: selectedOption.date,
        });
        if (clearOnSelect) {
          setSearchInput("");
          setOpenAutocompleteDropdown(false);
        }
        onSelect(response.content_id, response.content_type);
      } catch (error) {
        handleApiError(error);
      }
    } else {
      if (clearOnSelect) {
        setSearchInput("");
        setOpenAutocompleteDropdown(false);
      }
      onSelect(selectedOption.content_id, selectedOption.content_type);
    }
    setIsLoading(false);
  };

  // The search bar stays open after a quick add (the parent reports the outcome
  // via toast), so reset it for the next search once the add is handed off. A
  // failed import of an external result leaves the query in place to retry.
  // Signed-out users are prompted to log in before anything is imported.
  const handleQuickAdd = async (selectedOption, status) => {
    if (selectedOption.searchFor) return;
    if (!user) {
      openAuthDialog("Tracking content");
      return;
    }
    setIsLoading(true);
    setFocusedOption(null);
    setOpenAutocompleteDropdown(false);
    try {
      if (selectedOption.external_id) {
        const response = await addContent({
          contentType,
          identifier: selectedOption.external_id,
          title: selectedOption.title,
          person: selectedOption.person,
          publishedDate: selectedOption.date,
        });
        onQuickAdd(response.content_id, status, response.content_type);
      } else {
        onQuickAdd(
          selectedOption.content_id,
          status,
          selectedOption.content_type,
        );
      }
      setSearchInput("");
      setOptions([]);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Both open (never toggle) the menu: an Android long press fires the native
  // contextmenu event *and* the touch timer below, and a toggle would let whichever
  // lands second close the menu the first one opened.
  const handleContextMenuOpen = (event, option) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      selection: option,
    });
  };

  // iOS has no long-press contextmenu event, so the timer drives it there.
  const handleTouchStart = (event, option) => {
    event.preventDefault(); // Prevent scrolling during long press
    optionPressRef.current = true;
    timerRef.current = setTimeout(() => {
      const touch = event.touches[0];
      setLongPressedOption(option);
      setContextMenu({
        mouseX: touch.clientX - 120,
        mouseY: touch.clientY - 50,
        selection: option,
      });
    }, 500); // 500ms long-press threshold
  };

  const handleTouchEnd = () => {
    clearTimeout(timerRef.current);
    optionPressRef.current = false;
  };

  // Hands focus straight back to the input so the next search can be typed
  // immediately. Must run synchronously inside the tap/click that closes the menu —
  // Android only raises the keyboard for a programmatic focus within a user gesture.
  // (The Menu's own focus restore can't do this: on Android it returns focus to the
  // long-pressed option, which Chrome had focused, rather than the input.)
  const handleContextMenuClose = () => {
    optionPressRef.current = false;
    setLongPressedOption(null);
    setContextMenu(null);
    inputRef.current?.focus();
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2">{title}</Typography>
        <Box display="flex" alignItems="center">
          {(enableQuickAdd || contentType === "book") && (
            <IconButton
              onClick={(event) => setInfoPopupAnchorEl(event.currentTarget)}
              size="small"
              sx={{ ml: 1 }}
            >
              <InfoIcon />
            </IconButton>
          )}
          {fullscreen && (
            <IconButton onClick={onClose} size="small" sx={{ ml: 0 }}>
              {" "}
              <CloseIcon />{" "}
            </IconButton>
          )}
        </Box>
      </Stack>

      <Autocomplete
        id="autocomplete-search-bar"
        freeSolo
        fullWidth
        clearOnEscape
        // blurOnSelect
        inputValue={searchInput}
        options={options}
        open={openAutocompleteDropdown}
        ListboxProps={{ sx: { maxHeight: "35vh" } }} // Todo: replace with slotProps when upgrading to new version of MUI (current version does not support slotProps but next version requires it)!!!
        getOptionLabel={(option) => {
          if (
            typeof option === "object" &&
            (option.searchFor ||
              option.title ||
              option.noResults ||
              option.searchHint)
            // Required because when user presses enter without interacting with autocomplete dropdown, the option itself is passed
            // to getOptionLabel as a string and therefore doesn't have .value and .title properties
          ) {
            if (option.searchFor) return option.value;
            if (option.noResults) return option.label;
            if (option.searchHint) return "";
            return option.title;
          }
          return option;
        }}
        renderOption={(props, option) => {
          // Determines how the options are displayed in the Autocomplete dropdown

          if (option.searchFor) {
            return (
              <ButtonBase component="li" {...props} sx={{ width: "100%" }}>
                {/* Icon column matches the thumbnail width/margin above so the label lines up with result titles */}
                <Box
                  sx={{
                    width: 32,
                    height: 48,
                    flexShrink: 0,
                    mr: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchIcon color="action" />
                </Box>
                <Typography fontWeight="bold">{option.label}</Typography>
              </ButtonBase>
            );
          }
          if (option.noResults) {
            return (
              <li {...props} style={{ pointerEvents: "none" }}>
                <Typography fontWeight="bold">{option.label}</Typography>
              </li>
            );
          }
          if (option.searchHint) {
            return (
              <li {...props} style={{ pointerEvents: "none" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Can't find it? Try adding the author's name
                </Typography>
              </li>
            );
          }
          return (
            <ButtonBase
              component="li"
              className="allow-context-menu"
              {...props}
              {...(enableQuickAdd && {
                onContextMenu: (e) => handleContextMenuOpen(e, option),
                onTouchStart: (e) => handleTouchStart(e, option),
                onTouchEnd: handleTouchEnd,
                onTouchMove: handleTouchEnd,
              })}
              key={option.content_id || `ext-${option.external_id}`}
              style={{
                position: "relative",
                overflow: "hidden",
                backgroundColor:
                  longPressedOption === option && theme.palette.action.hover,
              }}
            >
              <SearchThumbnail
                src={
                  option.image_url &&
                  (contentType === "book"
                    ? `https://images.isbndb.com/covers/${option.image_url}.jpg`
                    : `https://image.tmdb.org/t/p/w185/${option.image_url}`)
                }
                alt={option.title}
              />

              {/* Text block */}
              <Box
                sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
              >
                {/* Line 1: title + year (books never show a date — pub. date isn't useful here) */}
                <Typography variant="body2" noWrap>
                  {option.title}
                  {option.date && contentType !== "book" && (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      {getYear(option.date)}
                    </Typography>
                  )}
                </Typography>

                {/* Line 2: director/creator/author */}
                {(option.person_names?.length > 0 || option.person) && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: "text.secondary" }}
                  >
                    {option.person_names?.length > 0
                      ? option.person_names.length === 1
                        ? option.person_names[0]
                        : `${option.person_names[0]} +${option.person_names.length - 1}`
                      : option.person}
                  </Typography>
                )}
              </Box>
            </ButtonBase>
          );
        }}
        filterOptions={(options) => options} // Needed to enable fuzzy matching to work, see here: https://stackoverflow.com/questions/68505566/autocomplete-not-rendering-as-expected-material-ui
        onChange={(event, value, reason) => {
          if (!value) return; // safety-guard
          // decide what the user meant
          if (typeof value === "object" && value.searchFor) {
            handleSearchFor(value.value); // “Search for …” pseudo-option
          } else if (typeof value === "object") {
            handleSelect(value); // regular catalogue item
          } else {
            handleSearchFor(value); // free-solo text -> search
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (clearOnSelect && reason === "reset") return;
          setSearchInput(newInputValue); // Update the search input based on user input
          setOpenAutocompleteDropdown(true); // Needed to ensure dropdown is visible when user starts typing
        }}
        onHighlightChange={(event, option, reason) => {
          // Only arrow-key highlights arm quick add. Mouse hover also highlights, and
          // the pointer crosses the top result on its way back to the input, which
          // left that result armed so typing f/s/a/q quick-added it.
          setFocusedOption(
            reason === "keyboard" && option !== null
              ? options.indexOf(option)
              : null,
          );
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
          if (focusedOption !== null && enableQuickAdd) {
            const selectedOption = options[focusedOption];
            if (["q", "s", "a", "f"].includes(event.key)) {
              event.preventDefault();
            }
            if (event.key === "q") {
              handleQuickAdd(selectedOption, "queued");
            } else if (event.key === "s") {
              handleQuickAdd(selectedOption, "started");
            } else if (event.key === "a") {
              handleQuickAdd(selectedOption, "abandoned");
            } else if (event.key === "f") {
              handleQuickAdd(selectedOption, "finished");
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            autoFocus
            hiddenLabel={true}
            // Book search also accepts ISBNs (routed to an ISBN lookup by the backend)
            placeholder={contentType === "book" ? "Search by title or ISBN" : undefined}
            onBlur={() => {
              if (!contextMenu && !optionPressRef.current) {
                setOpenAutocompleteDropdown(false);
              }
            }}
            size="small"
            variant="filled"
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              onClick: (e) => {
                e.stopPropagation();
                // Clicking back into the input means the user wants to type, so
                // disarm any arrow-key highlight left over from browsing results
                setFocusedOption(null);
              },
              onFocus: (e) => e.stopPropagation(),
            }}
          />
        )}
      />

      {isLoading && <LinearProgress />}

      {(enableQuickAdd || contentType === "book") && (
        <Popover
          id="mouse-over-popover"
          sx={{
            mt: 1,
          }}
          open={Boolean(infoPopupAnchorEl)}
          anchorEl={infoPopupAnchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          onClose={() => setInfoPopupAnchorEl(null)}
          disableRestoreFocus={process.env.NODE_ENV === "development"}
        >
          <Box sx={{ p: 2 }} maxWidth={300}>
            {contentType === "book" && (
              <>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", mb: 0.5 }}
                >
                  Refine your search:
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Default search is title only
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: enableQuickAdd ? 1.5 : 0 }}
                >
                  To include author, use "Title — Author" <br /> e.g. "Oliver
                  Twist — Charles Dickens"
                </Typography>
              </>
            )}
            {enableQuickAdd && (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Quick add:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  {isMobile ? (
                    <TouchAppIcon fontSize="small" />
                  ) : (
                    <MouseIcon fontSize="small" />
                  )}
                  <Typography variant="body2">
                    {!isMobile ? <b>Right-click</b> : <b>Long press</b>} a
                    search result to quickly queue, start, stop or finish it
                  </Typography>
                </Stack>
                {!isMobile && (
                  <Stack direction="row" spacing={1}>
                    <KeyboardIcon fontSize="small" />
                    <Typography variant="body2">
                      Use the <b>arrow keys</b> to highlight search result, then
                      press <b>q</b> to queue, <b>s</b> to start, <b>a</b> to
                      stop or <b>f</b> to finish
                    </Typography>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Popover>
      )}

      {/* Focus handling is left to handleContextMenuClose: enforce-focus would pull
      its input focus() back into the still-open menu, and restore-focus would then
      move it off the input again. */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        disableEnforceFocus
        disableRestoreFocus
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {statusOptionsArray.map((menuOption, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              handleQuickAdd(contextMenu.selection, menuOption.value);
              handleContextMenuClose();
            }}
            dense
          >
            <ListItemIcon sx={{ color: menuOption.color }}>
              {menuOption.icon}
            </ListItemIcon>
            <ListItemText>{menuOption.action}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// Cover/poster thumbnail for a search result. With no image, or one that fails to
// load, shows an empty outline in the same 2:3 shape so every row stays aligned.
function SearchThumbnail({ src, alt }) {
  const [failed, setFailed] = useState(false);
  const sx = {
    width: 32,
    height: 48,
    flexShrink: 0,
    mr: 1.5,
    borderRadius: 0.5,
    boxSizing: "border-box",
  };

  if (!src || failed) {
    return <Box sx={{ ...sx, border: 1, borderColor: "divider" }} />;
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      sx={{ ...sx, objectFit: "cover", bgcolor: "action.hover" }} // bgcolor shows while loading
    />
  );
}
